// src/components/login/signup-page.tsx
import { useState, FormEvent } from "react";
import { useAuth } from "../../hooks/login/useAuth";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { UpmodeLogo } from "./upmode-logo";
import {
  PasswordRequirements,
  checkPassword,
} from "./password-requirements";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  AlertCircle,
  Loader2,
  ArrowRight,
} from "lucide-react";

interface SignupPageProps {
  onSignupSuccess?: () => void;
  onNavigateToLogin?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

export function SignupPage({
  onSignupSuccess,
  onNavigateToLogin,
  onNavigateToTerms,
  onNavigateToPrivacy,
}: SignupPageProps) {
  const { signup, isLoading, error, clearError } = useAuth();

  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const pwChecks = checkPassword(password);
  const canSubmit = !isLoading && !!name && !!email && pwChecks.allValid;

  const baseInputStyle: React.CSSProperties = {
    height: 56,
    fontFamily: "var(--font-sans)",
    fontSize: "var(--text-lg)",
    borderRadius: "var(--radius)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--input-background)",
    boxSizing: "border-box",
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await signup({ name, email, password });
      onSignupSuccess?.();
    } catch {
      // surfaced via context.error
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        position: "relative",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, var(--alpha-teal-2) 0%, transparent 40%)
          `,
          opacity: 0.6,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(24px, 6vh, 72px) clamp(16px, 3vw, 24px)",
          position: "relative",
          zIndex: 1,
          boxSizing: "border-box",
        }}
      >
        <div style={{ width: "100%", maxWidth: 720 }}>
          {/* Logo + Title */}
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ display: "inline-flex", marginBottom: 20 }}>
              <UpmodeLogo />
            </div>
            <h1
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-4xl)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                lineHeight: "var(--leading-tight)",
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Create your account
            </h1>
          </div>

          {/* Card */}
          <div
            style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "clamp(24px, 4vh, 56px)",
              boxShadow: "var(--elevation-lg)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
                borderRadius: "16px 16px 0 0",
                opacity: 0.8,
              }}
            />

            {error && (
              <Alert
                variant="destructive"
                style={{
                  marginBottom: 20,
                  backgroundColor: "var(--infrared-coral-5)",
                  border: "1px solid var(--infrared-coral)",
                  borderRadius: "var(--radius)",
                }}
              >
                <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
                <AlertDescription
                  style={{
                    color: "var(--infrared-coral)",
                    whiteSpace: "pre-line",
                    lineHeight: 1.5,
                  }}
                >
                  {error.message}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div style={{ marginBottom: 22 }}>
                <label
                  htmlFor="name"
                  style={{
                    display: "block",
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-base)",
                    fontWeight: 600,
                    color: "var(--foreground)",
                    marginBottom: 10,
                  }}
                >
                  Full name
                </label>
                <div style={{ position: "relative" }}>
                  <User
                    style={{
                      position: "absolute",
                      left: 18, top: "50%", transform: "translateY(-50%)",
                      width: 20, height: 20,
                      color: "var(--muted-foreground)",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Jane Smith"
                    style={{ ...baseInputStyle, paddingLeft: 52 }}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 22 }}>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-base)",
                    fontWeight: 600,
                    color: "var(--foreground)",
                    marginBottom: 10,
                  }}
                >
                  Email address
                </label>
                <div style={{ position: "relative" }}>
                  <Mail
                    style={{
                      position: "absolute",
                      left: 18, top: "50%", transform: "translateY(-50%)",
                      width: 20, height: 20,
                      color: "var(--muted-foreground)",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="jane@company.com"
                    style={{ ...baseInputStyle, paddingLeft: 52 }}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor="password"
                  style={{
                    display: "block",
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-base)",
                    fontWeight: 600,
                    color: "var(--foreground)",
                    marginBottom: 10,
                  }}
                >
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <Lock
                    style={{
                      position: "absolute",
                      left: 18, top: "50%", transform: "translateY(-50%)",
                      width: 20, height: 20,
                      color: "var(--muted-foreground)",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Choose a strong password"
                    autoComplete="new-password"
                    style={{
                      ...baseInputStyle,
                      paddingLeft: 52,
                      paddingRight: 52,
                      border: `1px solid ${
                        password && !pwChecks.allValid
                          ? "var(--infrared-coral)"
                          : "var(--border)"
                      }`,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute",
                      right: 18, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer",
                      padding: 4,
                      color: "var(--muted-foreground)",
                      zIndex: 2,
                    }}
                  >
                    {showPassword ? (
                      <EyeOff style={{ width: 20, height: 20 }} />
                    ) : (
                      <Eye style={{ width: 20, height: 20 }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Shared password requirements checklist */}
              <div style={{ marginBottom: 22 }}>
                <PasswordRequirements password={password} />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: "100%",
                  height: 56,
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--text-lg)",
                  fontWeight: 600,
                  background: !canSubmit ? "var(--muted)" : "var(--alpha-teal)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 style={{ width: 20, height: 20 }} className="animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Create account
                    <ArrowRight style={{ width: 20, height: 20 }} />
                  </>
                )}
              </Button>
            </form>

            {/* Legal footer */}
            <div
              style={{
                textAlign: "center",
                marginTop: 22,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-xs)",
                color: "var(--muted-foreground)",
                lineHeight: "var(--leading-relaxed)",
              }}
            >
              By creating an account, you agree to our{" "}
              <button
                onClick={(e) => { e.preventDefault(); onNavigateToTerms?.(); }}
                style={{
                  background: "none", border: "none",
                  color: "var(--signal-blue)", cursor: "pointer", padding: 0,
                  fontFamily: "var(--font-sans)", fontSize: "var(--text-xs)",
                }}
              >
                Terms of Service
              </button>{" "}
              and{" "}
              <button
                onClick={(e) => { e.preventDefault(); onNavigateToPrivacy?.(); }}
                style={{
                  background: "none", border: "none",
                  color: "var(--signal-blue)", cursor: "pointer", padding: 0,
                  fontFamily: "var(--font-sans)", fontSize: "var(--text-xs)",
                }}
              >
                Privacy Policy
              </button>
              .
            </div>
          </div>

          {/* Login link */}
          <div
            style={{
              textAlign: "center",
              marginTop: 22,
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              color: "var(--muted-foreground)",
            }}
          >
            Already have an account?{" "}
            <button
              onClick={onNavigateToLogin}
              disabled={isLoading}
              style={{
                background: "none", border: "none",
                color: "var(--alpha-teal)", fontWeight: 600,
                cursor: "pointer", padding: 0,
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}